/* ============================================================
   Creative Carpentry Building & Remodeling, Inc.
   script.js — Professional Elevated (Variant 3)
   ============================================================ */

document.addEventListener('DOMContentLoaded', () => {
  // ---------- Header Scroll Effect ----------
  const header = document.querySelector('.site-header');
  if (header) {
    const onScroll = () => {
      header.classList.toggle('scrolled', window.scrollY > 50);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  // ---------- Mobile Navigation ----------
  const navToggle = document.querySelector('.nav-toggle');
  const mainNav = document.querySelector('.main-nav');
  if (navToggle && mainNav) {
    navToggle.addEventListener('click', () => {
      navToggle.classList.toggle('open');
      mainNav.classList.toggle('open');
      document.body.style.overflow = mainNav.classList.contains('open') ? 'hidden' : '';
    });

    // Close nav on link click
    mainNav.querySelectorAll('a').forEach(link => {
      link.addEventListener('click', () => {
        navToggle.classList.remove('open');
        mainNav.classList.remove('open');
        document.body.style.overflow = '';
      });
    });

    // Close nav on outside click
    document.addEventListener('click', (e) => {
      if (mainNav.classList.contains('open') && !mainNav.contains(e.target) && !navToggle.contains(e.target)) {
        navToggle.classList.remove('open');
        mainNav.classList.remove('open');
        document.body.style.overflow = '';
      }
    });
  }

  // ---------- Parallax Scrolling ----------
  const parallaxElements = document.querySelectorAll('.hero-bg, .parallax-bg');
  if (parallaxElements.length > 0 && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    const handleParallax = () => {
      const scrollY = window.scrollY;
      parallaxElements.forEach(el => {
        const speed = 0.5;
        const yPos = -(scrollY * speed);
        el.style.transform = `translateY(${yPos}px)`;
      });
    };

    window.addEventListener('scroll', handleParallax, { passive: true });
  }

  // ---------- Scroll Reveal Animation ----------
  const revealElements = document.querySelectorAll('.reveal');
  if (revealElements.length > 0) {
    const revealObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
          revealObserver.unobserve(entry.target);
        }
      });
    }, { 
      threshold: 0.1, 
      rootMargin: '0px 0px -50px 0px' 
    });

    revealElements.forEach(el => revealObserver.observe(el));
  }

  // ---------- Smooth Scrolling for Anchor Links ----------
  document.querySelectorAll('a[href^="#"]').forEach(anchor => {
    anchor.addEventListener('click', function (e) {
      e.preventDefault();
      const targetId = this.getAttribute('href').substring(1);
      const targetElement = document.getElementById(targetId);
      if (targetElement) {
        const headerOffset = document.querySelector('.site-header').offsetHeight;
        const elementPosition = targetElement.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerOffset - 20;

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    });
  });

  // ---------- Gallery Lightbox ----------
  initGalleryLightbox();

  // ---------- Contact Form Handler ----------
  const contactForm = document.getElementById('contact-form');
  if (contactForm) {
    contactForm.addEventListener('submit', handleContactForm);
  }

  // ---------- Gallery Filter ----------
  initGalleryFilter();

  // ---------- Step Wizard (Estimate Page) ----------
  initStepWizard();

  // ---------- Timeline Animation ----------
  initTimelineAnimation();

  // ---------- Before/After Comparison Sliders ----------
  initComparisonSliders();

  // ---------- FAQ Functionality ----------
  initFAQ();
});

// ---------- Gallery Lightbox ----------
function initGalleryLightbox() {
  const galleryItems = document.querySelectorAll('.gallery-item');
  
  if (galleryItems.length === 0) return;

  // Create lightbox HTML
  const lightbox = document.createElement('div');
  lightbox.className = 'lightbox';
  lightbox.innerHTML = `
    <div class="lightbox-content">
      <button class="lightbox-close" aria-label="Close lightbox">&times;</button>
      <button class="lightbox-prev" aria-label="Previous image">&#8249;</button>
      <button class="lightbox-next" aria-label="Next image">&#8250;</button>
      <img class="lightbox-image" alt="">
      <div class="lightbox-caption"></div>
    </div>
    <div class="lightbox-overlay"></div>
  `;
  document.body.appendChild(lightbox);

  // Add lightbox styles
  const lightboxStyles = `
    <style>
      .lightbox {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        z-index: 10000;
        display: flex;
        align-items: center;
        justify-content: center;
        opacity: 0;
        visibility: hidden;
        transition: opacity 0.3s ease, visibility 0.3s ease;
      }
      
      .lightbox.active {
        opacity: 1;
        visibility: visible;
      }
      
      .lightbox-overlay {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(27, 45, 58, 0.95);
        backdrop-filter: blur(10px);
      }
      
      .lightbox-content {
        position: relative;
        max-width: 90vw;
        max-height: 90vh;
        z-index: 10001;
      }
      
      .lightbox-image {
        max-width: 100%;
        max-height: 85vh;
        object-fit: contain;
        border-radius: 8px;
        box-shadow: 0 8px 32px rgba(0,0,0,0.3);
      }
      
      .lightbox-close,
      .lightbox-prev,
      .lightbox-next {
        position: absolute;
        background: rgba(250,250,247,0.9);
        border: none;
        width: 50px;
        height: 50px;
        border-radius: 50%;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 24px;
        color: var(--primary);
        transition: all 0.2s ease;
        backdrop-filter: blur(10px);
      }
      
      .lightbox-close {
        top: -60px;
        right: 0;
        font-size: 30px;
      }
      
      .lightbox-prev {
        left: -70px;
        top: 50%;
        transform: translateY(-50%);
      }
      
      .lightbox-next {
        right: -70px;
        top: 50%;
        transform: translateY(-50%);
      }
      
      .lightbox-close:hover,
      .lightbox-prev:hover,
      .lightbox-next:hover {
        background: var(--accent);
        color: white;
        transform: scale(1.1);
      }
      
      .lightbox-prev:hover {
        transform: translateY(-50%) scale(1.1);
      }
      
      .lightbox-next:hover {
        transform: translateY(-50%) scale(1.1);
      }
      
      .lightbox-caption {
        position: absolute;
        bottom: -50px;
        left: 0;
        right: 0;
        text-align: center;
        color: white;
        font-size: 14px;
        padding: 10px;
      }
      
      @media (max-width: 768px) {
        .lightbox-prev, .lightbox-next {
          width: 40px;
          height: 40px;
          font-size: 18px;
        }
        
        .lightbox-prev { left: -50px; }
        .lightbox-next { right: -50px; }
        .lightbox-close { top: -50px; width: 40px; height: 40px; font-size: 24px; }
      }
    </style>
  `;
  document.head.insertAdjacentHTML('beforeend', lightboxStyles);

  let currentIndex = 0;
  const images = Array.from(galleryItems).map(item => {
    const img = item.querySelector('img');
    return {
      src: img.src,
      alt: img.alt
    };
  });

  function showImage(index) {
    const lightboxImg = lightbox.querySelector('.lightbox-image');
    const lightboxCaption = lightbox.querySelector('.lightbox-caption');
    
    lightboxImg.src = images[index].src;
    lightboxImg.alt = images[index].alt;
    lightboxCaption.textContent = images[index].alt;
    currentIndex = index;
  }

  function openLightbox(index) {
    showImage(index);
    lightbox.classList.add('active');
    document.body.style.overflow = 'hidden';
  }

  function closeLightbox() {
    lightbox.classList.remove('active');
    document.body.style.overflow = '';
  }

  // Event listeners
  galleryItems.forEach((item, index) => {
    item.addEventListener('click', () => openLightbox(index));
  });

  lightbox.querySelector('.lightbox-close').addEventListener('click', closeLightbox);
  lightbox.querySelector('.lightbox-overlay').addEventListener('click', closeLightbox);

  lightbox.querySelector('.lightbox-prev').addEventListener('click', () => {
    const newIndex = currentIndex === 0 ? images.length - 1 : currentIndex - 1;
    showImage(newIndex);
  });

  lightbox.querySelector('.lightbox-next').addEventListener('click', () => {
    const newIndex = currentIndex === images.length - 1 ? 0 : currentIndex + 1;
    showImage(newIndex);
  });

  // Keyboard navigation
  document.addEventListener('keydown', (e) => {
    if (!lightbox.classList.contains('active')) return;
    
    if (e.key === 'Escape') closeLightbox();
    if (e.key === 'ArrowLeft') lightbox.querySelector('.lightbox-prev').click();
    if (e.key === 'ArrowRight') lightbox.querySelector('.lightbox-next').click();
  });
}

// ---------- Contact Form Handler ----------
function handleContactForm(e) {
  e.preventDefault();
  
  // Get form data
  const formData = new FormData(e.target);
  const data = Object.fromEntries(formData.entries());
  
  // Simple validation
  if (!data.name || !data.email) {
    showFormMessage('Please fill in all required fields.', 'error');
    return;
  }
  
  if (!isValidEmail(data.email)) {
    showFormMessage('Please enter a valid email address.', 'error');
    return;
  }
  
  // Simulate form submission
  const submitBtn = e.target.querySelector('button[type="submit"]');
  const originalText = submitBtn.textContent;
  submitBtn.textContent = 'Sending...';
  submitBtn.disabled = true;
  
  setTimeout(() => {
    showFormMessage('Thank you for your message! We\'ll get back to you soon.', 'success');
    e.target.reset();
    submitBtn.textContent = originalText;
    submitBtn.disabled = false;
  }, 2000);
}

function showFormMessage(message, type) {
  const existing = document.querySelector('.form-message');
  if (existing) existing.remove();
  
  const messageDiv = document.createElement('div');
  messageDiv.className = `form-message ${type}`;
  messageDiv.textContent = message;
  
  const form = document.getElementById('contact-form');
  form.insertBefore(messageDiv, form.firstChild);
  
  // Add styles if they don't exist
  if (!document.querySelector('#form-message-styles')) {
    const styles = `
      <style id="form-message-styles">
        .form-message {
          padding: 15px;
          border-radius: 8px;
          margin-bottom: 20px;
          font-weight: 500;
        }
        .form-message.success {
          background: #d4edda;
          color: #155724;
          border: 1px solid #c3e6cb;
        }
        .form-message.error {
          background: #f8d7da;
          color: #721c24;
          border: 1px solid #f5c6cb;
        }
      </style>
    `;
    document.head.insertAdjacentHTML('beforeend', styles);
  }
  
  setTimeout(() => messageDiv.remove(), 5000);
}

function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

// ---------- Gallery Filter ----------
function initGalleryFilter() {
  const filterButtons = document.querySelectorAll('.filter-btn');
  const galleryItems = document.querySelectorAll('.gallery-item');
  
  if (filterButtons.length === 0 || galleryItems.length === 0) return;
  
  filterButtons.forEach(button => {
    button.addEventListener('click', () => {
      const filter = button.dataset.filter;
      
      // Update active button
      filterButtons.forEach(btn => btn.classList.remove('active'));
      button.classList.add('active');
      
      // Filter gallery items
      galleryItems.forEach(item => {
        if (filter === 'all' || item.dataset.category === filter) {
          item.style.display = 'block';
          setTimeout(() => item.style.opacity = '1', 50);
        } else {
          item.style.opacity = '0';
          setTimeout(() => item.style.display = 'none', 300);
        }
      });
    });
  });
}

// ---------- Step Wizard (Estimate Page) ----------
function initStepWizard() {
  const wizard = document.querySelector('.step-wizard');
  if (!wizard) return;
  
  let currentStep = 1;
  const totalSteps = 4;
  
  const steps = {
    1: { title: 'Select Project Type', options: [] },
    2: { title: 'Choose Size/Scope', options: [] },
    3: { title: 'Select Finish Level', options: [] },
    4: { title: 'Your Estimate', options: [] }
  };
  
  const selections = {};
  
  // Price ranges database
  const priceRanges = {
    'kitchen-remodel': {
      small: { standard: [15000, 25000], premium: [25000, 45000], luxury: [45000, 90000] },
      medium: { standard: [20000, 35000], premium: [35000, 65000], luxury: [65000, 120000] },
      large: { standard: [25000, 45000], premium: [45000, 85000], luxury: [85000, 150000] }
    },
    'bathroom-remodel': {
      small: { standard: [8000, 15000], premium: [15000, 25000], luxury: [25000, 45000] },
      medium: { standard: [12000, 20000], premium: [20000, 35000], luxury: [35000, 60000] },
      large: { standard: [15000, 25000], premium: [25000, 45000], luxury: [45000, 80000] }
    },
    'closet-design': {
      small: { standard: [2000, 4000], premium: [4000, 8000], luxury: [8000, 15000] },
      medium: { standard: [3000, 6000], premium: [6000, 12000], luxury: [12000, 20000] },
      large: { standard: [5000, 10000], premium: [10000, 18000], luxury: [18000, 30000] }
    },
    'deck-outdoor': {
      small: { standard: [8000, 15000], premium: [15000, 30000], luxury: [30000, 60000] },
      medium: { standard: [15000, 30000], premium: [30000, 60000], luxury: [60000, 100000] },
      large: { standard: [25000, 50000], premium: [50000, 90000], luxury: [90000, 150000] }
    },
    'new-construction': {
      small: { standard: [150, 200], premium: [200, 300], luxury: [300, 500] },
      medium: { standard: [175, 225], premium: [225, 350], luxury: [350, 550] },
      large: { standard: [200, 275], premium: [275, 425], luxury: [425, 650] }
    },
    'fireplace-surround': {
      small: { standard: [2000, 4000], premium: [4000, 8000], luxury: [8000, 15000] },
      medium: { standard: [3000, 6000], premium: [6000, 12000], luxury: [12000, 20000] },
      large: { standard: [4000, 8000], premium: [8000, 15000], luxury: [15000, 25000] }
    },
    'trim-crown-molding': {
      small: { standard: [1000, 2500], premium: [2500, 5000], luxury: [5000, 10000] },
      medium: { standard: [2000, 4000], premium: [4000, 8000], luxury: [8000, 15000] },
      large: { standard: [3000, 6000], premium: [6000, 12000], luxury: [12000, 20000] }
    },
    'custom-built-ins': {
      small: { standard: [2500, 5000], premium: [5000, 10000], luxury: [10000, 20000] },
      medium: { standard: [4000, 8000], premium: [8000, 15000], luxury: [15000, 30000] },
      large: { standard: [6000, 12000], premium: [12000, 25000], luxury: [25000, 50000] }
    }
  };
  
  function updateProgressBar() {
    const progress = ((currentStep - 1) / (totalSteps - 1)) * 100;
    const progressFill = document.querySelector('.step-progress-fill');
    if (progressFill) {
      progressFill.style.width = `${progress}%`;
    }
    
    // Update step indicators
    document.querySelectorAll('.step-indicator').forEach((indicator, index) => {
      const stepNum = index + 1;
      if (stepNum < currentStep) {
        indicator.classList.add('completed');
        indicator.classList.remove('active');
      } else if (stepNum === currentStep) {
        indicator.classList.add('active');
        indicator.classList.remove('completed');
      } else {
        indicator.classList.remove('active', 'completed');
      }
    });
  }
  
  function renderStep() {
    const content = document.querySelector('.step-content');
    if (!content) return;
    
    if (currentStep === 1) {
      content.innerHTML = `
        <h2>What type of project do you have in mind?</h2>
        <div class="option-grid">
          <div class="option-card" data-value="new-construction">
            <h4>New Construction</h4>
            <p>Custom home building from foundation to finish</p>
          </div>
          <div class="option-card" data-value="kitchen-remodel">
            <h4>Kitchen Remodel</h4>
            <p>Transform the heart of your home</p>
          </div>
          <div class="option-card" data-value="bathroom-remodel">
            <h4>Bathroom Remodel</h4>
            <p>Create a spa-like retreat</p>
          </div>
          <div class="option-card" data-value="closet-design">
            <h4>Closet Design</h4>
            <p>Custom storage solutions</p>
          </div>
          <div class="option-card" data-value="deck-outdoor">
            <h4>Deck & Outdoor</h4>
            <p>Extend your living space outdoors</p>
          </div>
          <div class="option-card" data-value="fireplace-surround">
            <h4>Fireplace Surround</h4>
            <p>Beautiful focal point craftsmanship</p>
          </div>
          <div class="option-card" data-value="trim-crown-molding">
            <h4>Trim & Crown Molding</h4>
            <p>Elegant finishing touches</p>
          </div>
          <div class="option-card" data-value="custom-built-ins">
            <h4>Custom Built-ins</h4>
            <p>Bookshelves, entertainment centers, and more</p>
          </div>
        </div>
        <div class="step-actions">
          <div></div>
          <button class="btn btn-primary next-btn" disabled>Next Step</button>
        </div>
      `;
    } else if (currentStep === 2) {
      const projectType = selections.projectType;
      let sizeOptions = [];
      
      if (projectType === 'new-construction') {
        sizeOptions = [
          { value: 'small', title: 'Small Home', desc: 'Up to 1,500 sq ft' },
          { value: 'medium', title: 'Medium Home', desc: '1,500 - 3,000 sq ft' },
          { value: 'large', title: 'Large Home', desc: '3,000+ sq ft' }
        ];
      } else {
        sizeOptions = [
          { value: 'small', title: 'Small Project', desc: 'Basic scope, essential updates' },
          { value: 'medium', title: 'Medium Project', desc: 'Moderate scope, some custom elements' },
          { value: 'large', title: 'Large Project', desc: 'Extensive scope, premium features' }
        ];
      }
      
      content.innerHTML = `
        <h2>What size or scope best describes your project?</h2>
        <div class="option-grid">
          ${sizeOptions.map(option => `
            <div class="option-card" data-value="${option.value}">
              <h4>${option.title}</h4>
              <p>${option.desc}</p>
            </div>
          `).join('')}
        </div>
        <div class="step-actions">
          <button class="btn btn-secondary prev-btn">Previous</button>
          <button class="btn btn-primary next-btn" disabled>Next Step</button>
        </div>
      `;
    } else if (currentStep === 3) {
      content.innerHTML = `
        <h2>What finish level are you looking for?</h2>
        <div class="option-grid">
          <div class="option-card" data-value="standard">
            <h4>Standard</h4>
            <p>Quality materials, clean finishes, great value</p>
          </div>
          <div class="option-card" data-value="premium">
            <h4>Premium</h4>
            <p>Upgraded materials, custom details, refined look</p>
          </div>
          <div class="option-card" data-value="luxury">
            <h4>Luxury</h4>
            <p>Top-tier materials, custom everything, showpiece quality</p>
          </div>
        </div>
        <div class="step-actions">
          <button class="btn btn-secondary prev-btn">Previous</button>
          <button class="btn btn-primary next-btn" disabled>Get My Estimate</button>
        </div>
      `;
    } else if (currentStep === 4) {
      const estimate = calculateEstimate();
      content.innerHTML = `
        <div class="estimate-result">
          <h2>Your Estimated Project Cost</h2>
          <div class="price-range">${estimate.range}</div>
          <div class="timeline-info">Typical Timeline: ${estimate.timeline}</div>
          <a href="contact.html" class="btn btn-primary btn-large">Get a Precise Quote</a>
          <div class="disclaimer">
            <strong>Please Note:</strong> These estimates are approximate ranges based on typical projects in Brunswick County, NC. Your actual cost may vary based on specific requirements, materials, site conditions, and current market pricing. Contact us for a personalized consultation and detailed quote.
          </div>
        </div>
        <div class="step-actions">
          <button class="btn btn-secondary prev-btn">Previous</button>
          <button class="btn btn-outline-dark" onclick="window.location.reload()">Start Over</button>
        </div>
      `;
    }
    
    // Add event listeners to option cards
    document.querySelectorAll('.option-card').forEach(card => {
      card.addEventListener('click', () => {
        document.querySelectorAll('.option-card').forEach(c => c.classList.remove('selected'));
        card.classList.add('selected');
        
        const nextBtn = document.querySelector('.next-btn');
        if (nextBtn) nextBtn.disabled = false;
        
        // Store selection
        if (currentStep === 1) selections.projectType = card.dataset.value;
        if (currentStep === 2) selections.size = card.dataset.value;
        if (currentStep === 3) selections.finish = card.dataset.value;
      });
    });
    
    // Add event listeners to navigation buttons
    const prevBtn = document.querySelector('.prev-btn');
    const nextBtn = document.querySelector('.next-btn');
    
    if (prevBtn) {
      prevBtn.addEventListener('click', () => {
        currentStep--;
        renderStep();
        updateProgressBar();
      });
    }
    
    if (nextBtn) {
      nextBtn.addEventListener('click', () => {
        currentStep++;
        renderStep();
        updateProgressBar();
      });
    }
  }
  
  function calculateEstimate() {
    const { projectType, size, finish } = selections;
    const ranges = priceRanges[projectType];
    
    if (!ranges) return { range: 'Contact us for a quote', timeline: '2-12 weeks' };
    
    const [low, high] = ranges[size][finish];
    
    let rangeText;
    if (projectType === 'new-construction') {
      rangeText = `$${low} - $${high} per sq ft`;
    } else {
      rangeText = `$${low.toLocaleString()} - $${high.toLocaleString()}`;
    }
    
    const timelines = {
      'kitchen-remodel': '3-8 weeks',
      'bathroom-remodel': '2-6 weeks',
      'closet-design': '1-3 weeks',
      'deck-outdoor': '2-6 weeks',
      'new-construction': '4-12 months',
      'fireplace-surround': '1-3 weeks',
      'trim-crown-molding': '1-2 weeks',
      'custom-built-ins': '2-6 weeks'
    };
    
    return {
      range: rangeText,
      timeline: timelines[projectType] || '2-8 weeks'
    };
  }
  
  // Initialize the wizard
  renderStep();
  updateProgressBar();
}

// ---------- Timeline Animation ----------
function initTimelineAnimation() {
  const timelineItems = document.querySelectorAll('.timeline-item');
  
  if (timelineItems.length === 0) return;
  
  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.style.opacity = '1';
        entry.target.style.transform = 'translateX(0)';
      }
    });
  }, { threshold: 0.3 });
  
  timelineItems.forEach((item, index) => {
    item.style.opacity = '0';
    item.style.transition = 'opacity 0.6s ease, transform 0.6s ease';
    
    if (index % 2 === 0) {
      item.style.transform = 'translateX(-50px)';
    } else {
      item.style.transform = 'translateX(50px)';
    }
    
    observer.observe(item);
  });
}

// ---------- Before/After Comparison Sliders ----------
function initComparisonSliders() {
  const sliders = document.querySelectorAll('.comparison-slider');
  
  sliders.forEach(slider => {
    const handle = slider.querySelector('.comparison-handle');
    const beforeImage = slider.querySelector('.comparison-before');
    let isActive = false;
    
    function moveSlider(e) {
      const rect = slider.getBoundingClientRect();
      const x = e.type.includes('touch') ? e.touches[0].clientX : e.clientX;
      const position = ((x - rect.left) / rect.width) * 100;
      const clampedPosition = Math.min(Math.max(position, 0), 100);
      
      handle.style.left = `${clampedPosition}%`;
      beforeImage.style.clipPath = `inset(0 ${100 - clampedPosition}% 0 0)`;
    }
    
    function updateSlider(e) {
      if (!isActive) return;
      moveSlider(e);
    }
    
    function startDragging(e) {
      // Stop the browser starting a text/image selection during the drag
      if (e.type === 'mousedown') e.preventDefault();
      isActive = true;
      slider.classList.add('dragging');
      updateSlider(e);
    }
    
    function stopDragging() {
      isActive = false;
      slider.classList.remove('dragging');
    }
    
    // Mouse events
    handle.addEventListener('mousedown', startDragging);
    document.addEventListener('mousemove', updateSlider);
    document.addEventListener('mouseup', stopDragging);
    
    // Touch events
    handle.addEventListener('touchstart', startDragging);
    document.addEventListener('touchmove', updateSlider, { passive: false });
    document.addEventListener('touchend', stopDragging);
    
    // Click to position
    slider.addEventListener('click', (e) => {
      if (e.target === handle) return;
      moveSlider(e);
    });
    
    // Add dragging cursor styles
    const draggingStyles = `
      <style>
        .comparison-slider.dragging {
          cursor: ew-resize;
        }
        .comparison-slider.dragging * {
          cursor: ew-resize !important;
        }
      </style>
    `;
    if (!document.querySelector('#comparison-drag-styles')) {
      document.head.insertAdjacentHTML('beforeend', draggingStyles.replace('<style>', '<style id="comparison-drag-styles">'));
    }
  });
}

// ---------- FAQ Functionality ----------
function initFAQ() {
  const faqItems = document.querySelectorAll('.faq-item');
  const searchInput = document.querySelector('.faq-search input');
  const categoryFilters = document.querySelectorAll('.category-filter');
  
  // FAQ Accordion
  faqItems.forEach(item => {
    const question = item.querySelector('.faq-question');
    question.addEventListener('click', () => {
      const isOpen = item.classList.contains('open');
      
      // Close all other items
      faqItems.forEach(otherItem => {
        if (otherItem !== item) {
          otherItem.classList.remove('open');
        }
      });
      
      // Toggle current item
      item.classList.toggle('open', !isOpen);
    });
  });
  
  // FAQ Search
  if (searchInput) {
    searchInput.addEventListener('input', (e) => {
      const query = e.target.value.toLowerCase();
      
      faqItems.forEach(item => {
        const question = item.querySelector('.faq-question').textContent.toLowerCase();
        const answer = item.querySelector('.faq-answer p').textContent.toLowerCase();
        
        if (question.includes(query) || answer.includes(query)) {
          item.style.display = 'block';
        } else {
          item.style.display = 'none';
        }
      });
      
      // Hide category titles if no visible items
      document.querySelectorAll('.faq-category').forEach(category => {
        const visibleItems = category.querySelectorAll('.faq-item[style="display: block"], .faq-item:not([style*="display: none"])');
        category.style.display = visibleItems.length > 0 ? 'block' : 'none';
      });
    });
  }
  
  // Category Filters
  categoryFilters.forEach(filter => {
    filter.addEventListener('click', () => {
      const category = filter.dataset.category;
      
      // Update active filter
      categoryFilters.forEach(f => f.classList.remove('active'));
      filter.classList.add('active');
      
      // Show/hide categories
      document.querySelectorAll('.faq-category').forEach(cat => {
        if (category === 'all' || cat.dataset.category === category) {
          cat.style.display = 'block';
        } else {
          cat.style.display = 'none';
        }
      });
      
      // Clear search when filtering
      if (searchInput) searchInput.value = '';
      faqItems.forEach(item => item.style.display = 'block');
    });
  });
}

// ---------- Utility Functions ----------
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

// ---------- Enhanced Scroll Performance ----------
const throttle = (func, limit) => {
  let inThrottle;
  return function() {
    const args = arguments;
    const context = this;
    if (!inThrottle) {
      func.apply(context, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
};

// Apply throttling to scroll-heavy functions
window.addEventListener('scroll', throttle(() => {
  // Consolidated scroll handling for performance
}, 16)); // ~60fps

// ---------- Accessibility Enhancements ----------
// Skip to content link
document.addEventListener('DOMContentLoaded', () => {
  const skipLink = document.createElement('a');
  skipLink.href = '#main';
  skipLink.textContent = 'Skip to main content';
  skipLink.className = 'sr-only';
  skipLink.style.cssText = `
    position: absolute;
    top: -40px;
    left: 6px;
    z-index: 10001;
    padding: 8px;
    background: var(--primary);
    color: white;
    text-decoration: none;
    border-radius: 4px;
    transition: top 0.2s;
  `;
  
  skipLink.addEventListener('focus', () => {
    skipLink.style.top = '6px';
  });
  
  skipLink.addEventListener('blur', () => {
    skipLink.style.top = '-40px';
  });
  
  document.body.insertBefore(skipLink, document.body.firstChild);
  
  // Add main landmark if not present
  const main = document.querySelector('main');
  if (main && !main.id) {
    main.id = 'main';
  }
});

// ---------- Blog Accordion ----------
function initBlogAccordion() {
  const articles = document.querySelectorAll('.blog-article');
  if (articles.length === 0) return;

  articles.forEach(article => {
    const header = article.querySelector('.blog-article-header');
    header.addEventListener('click', () => {
      const isOpen = article.classList.contains('open');
      articles.forEach(a => { if (a !== article) a.classList.remove('open'); });
      article.classList.toggle('open', !isOpen);
    });
  });
}

// Initialize blog accordion on DOMContentLoaded
document.addEventListener('DOMContentLoaded', () => {
  initBlogAccordion();
});

// ---------- Loading Performance ----------
// Lazy load images that aren't immediately visible
document.addEventListener('DOMContentLoaded', () => {
  if ('IntersectionObserver' in window) {
    const imageObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const img = entry.target;
          if (img.dataset.src) {
            img.src = img.dataset.src;
            img.removeAttribute('data-src');
            imageObserver.unobserve(img);
          }
        }
      });
    });

    document.querySelectorAll('img[data-src]').forEach(img => {
      imageObserver.observe(img);
    });
  }
});